import React, { useRef, useState, useEffect } from 'react';
import {
  FiUser,
  FiCreditCard,
  FiSave,
  FiLoader,
  FiX,
  FiFileText,
  FiLogOut,
  FiLayers,
  FiPause,
  FiShield,
  FiTrash2,
  FiBookOpen,
} from 'react-icons/fi';
import Sidebar from '../Sidebar';
import ProfilePicture from '../../components/ProfilePicture';
import ToeflActivation from '../exams/toefl/Activation';
import {
  API_BASE_URL,
  toastdisplay,
  checkTokenExpiration,
  logout,
  getAuthHeaders,
  clearAllCaches,
  clearSyncableLocalStorage,
} from '../../utils/utils';
import {
  AUTH_EVENT_TYPES,
  getStoredUser,
  publishAuthEvent,
  requestBackendLogout,
  setAuthenticatedSession,
} from '../../utils/authSession';
import {
  buildPhoneDialCodePrefix,
  getPhoneCountryDefault,
} from '../../utils/countryPhoneDefaults';
import {
  cancelProgressHold,
  createProgressHold,
  fetchProgressHolds,
  persistProgressHoldsInSession,
  updateProgressHold,
} from '../../utils/progressHold';
import './Settings.css';

const SETTINGS_TABS = new Set([
  'personal',
  'billing',
  'progression',
  'toefl',
  'privacy',
]);
const COACH_HIDDEN_TABS = new Set(['billing', 'progression', 'toefl']);

function getInitialSettingsTab() {
  try {
    const tab = String(
      new URLSearchParams(window.location.search).get('tab') || ''
    ).toLowerCase();
    if (SETTINGS_TABS.has(tab)) return tab;
  } catch {
    /* keep default */
  }
  return 'personal';
}

const Settings = () => {
  // --- STATE MANAGEMENT ---
  const [user, setUser] = useState(getStoredUser());
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [sessionExpired, setSessionExpired] = useState(false);
  const [activeTab, setActiveTab] = useState(getInitialSettingsTab);
  const isCoach = user?.role?.name === 'coach';

  // Personal Info State
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [language, setLanguage] = useState('');
  const [avatarName, setAvatarName] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [availability, setAvailability] = useState('');
  const [profilePictureFile, setProfilePictureFile] = useState(null);

  // Billing State
  const [billingMonths, setBillingMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [activePremiumSubscriptions, setActivePremiumSubscriptions] = useState(
    () => {
      try {
        const u = getStoredUser();
        return u?.active_premium_subscriptions || [];
      } catch {
        return [];
      }
    }
  );
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');
  const [progressHolds, setProgressHolds] = useState([]);
  const [currentProgressHold, setCurrentProgressHold] = useState(null);
  const [holdStartDate, setHoldStartDate] = useState('');
  const [holdEndDate, setHoldEndDate] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [holdLoading, setHoldLoading] = useState(false);
  const [holdSaving, setHoldSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletePhrase, setDeletePhrase] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteAcknowledged, setDeleteAcknowledged] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const hasAppliedPhoneDefaultRef = useRef(false);

  // --- DATA FETCHING & INITIALIZATION ---
  useEffect(() => {
    // Populate personal info from user session
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setAvatarName(user.avatar_name || '');
      setPhoneNumber((current) => user.phone_number || current || '');
      setLanguage(user.language || 'en');
      setProfilePicture(user.profile_picture || '');
      setLinkedin(user.linkedin_profile || '');
      setAvailability(user.availabilities || '');
    }
  }, [user]);

  useEffect(() => {
    if (isCoach && COACH_HIDDEN_TABS.has(activeTab)) {
      setActiveTab('personal');
    }
  }, [isCoach, activeTab]);

  useEffect(() => {
    if (phoneNumber.trim() || hasAppliedPhoneDefaultRef.current) return undefined;

    let cancelled = false;

    getPhoneCountryDefault().then((phoneDefault) => {
      if (cancelled) return;
      hasAppliedPhoneDefaultRef.current = true;
      const prefix = buildPhoneDialCodePrefix(phoneDefault);
      if (!prefix) return;
      setPhoneNumber((current) => (current.trim() ? current : prefix));
    });

    return () => {
      cancelled = true;
    };
  }, [phoneNumber]);

  // Refresh user session with profile (includes active_premium_subscriptions)
  useEffect(() => {
    if (!user?.email || activeTab !== 'billing') return;

    const loadProfile = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/user/profile?userEmail=${encodeURIComponent(user.email)}`,
          { headers: getAuthHeaders() }
        );
        const data = await res.json();
        if (data?.user) {
          setAuthenticatedSession(data.user);
          setUser(data.user);
          setActivePremiumSubscriptions(
            data.user.active_premium_subscriptions || []
          );
        }
      } catch {
        /* keep existing session */
      }
    };

    loadProfile();
  }, [user?.email, activeTab]);

  useEffect(() => {
    if (!user?.email || activeTab !== 'progression') return;

    let cancelled = false;
    const loadHolds = async () => {
      setHoldLoading(true);
      try {
        const payload = await fetchProgressHolds();
        if (cancelled) return;
        setProgressHolds(payload.holds || []);
        setCurrentProgressHold(payload.current || null);
        persistProgressHoldsInSession(payload);
        if (payload.current) {
          setHoldStartDate(payload.current.start_date || '');
          setHoldEndDate(payload.current.end_date || '');
          setHoldReason(payload.current.reason || '');
        }
      } catch (error) {
        if (!cancelled) {
          setToast({
            show: true,
            message: error.message || 'Failed to load progress holds.',
            success: false,
          });
        }
      } finally {
        if (!cancelled) {
          setHoldLoading(false);
        }
      }
    };

    loadHolds();
    return () => {
      cancelled = true;
    };
  }, [user?.email, activeTab]);

  // Fetch billing data
  useEffect(() => {
    if (!user?.email || activeTab !== 'billing') return;

    setBillingLoading(true);
    setBillingError('');

    const parseBillingResponse = async (res, fallbackMessage) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || fallbackMessage);
      }
      return data;
    };

    const fetchInvoices = fetch(
      `${API_BASE_URL}/api/billing/invoices?user_email=${user.email}&limit=100`,
      { headers: getAuthHeaders() }
    ).then((res) => parseBillingResponse(res, 'Failed to load invoices'));

    const fetchSubscription = fetch(
      `${API_BASE_URL}/api/billing/subscription?user_email=${user.email}`,
      { headers: getAuthHeaders() }
    ).then((res) => parseBillingResponse(res, 'Failed to load subscription'));

    Promise.all([fetchInvoices, fetchSubscription])
      .then(([invoiceData, subData]) => {
        if (invoiceData.success) {
          setInvoices(invoiceData.invoices || []);
          setBillingMonths(invoiceData.past_months || []);
          if (invoiceData.past_months?.length > 0) {
            setSelectedMonth(invoiceData.past_months[0]);
          }
        } else {
          setBillingError(invoiceData.detail || 'Failed to load billing data');
        }

        if (subData.success) {
          setSubscription(subData.subscription);
          setActivePremiumSubscriptions(
            subData.active_premium_subscriptions || []
          );
        }
      })
      .catch((error) =>
        setBillingError(error?.message || 'Failed to load billing information.')
      )
      .finally(() => setBillingLoading(false));
  }, [user?.email, activeTab]);

  // --- HELPER FUNCTIONS ---
  const isPremiumUser = () => {
    if (user?.is_premium) return true;
    if (activePremiumSubscriptions?.length > 0) return true;
    if (user?.active_premium_subscriptions?.length > 0) return true;
    return (
      subscription &&
      ['active', 'trialing'].includes(String(subscription.status || '').toLowerCase())
    );
  };

  const formatSubscriptionDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const formatSubscriptionAmount = (sub) => {
    if (sub.amount_paid == null) return '—';

    const amount = Number(sub.amount_paid);
    if (Number.isNaN(amount)) return '—';

    const currency = String(sub.currency || '')
      .trim()
      .toUpperCase();

    return currency ? `${amount.toFixed(2)} ${currency}` : amount.toFixed(2);
  };

  const formatSubscriptionStatus = (status) => {
    if (!status) return 'Unknown';

    return String(status)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const getSubscriptionStatusClassName = (status) => {
    const normalizedStatus = String(status || '').toLowerCase();

    if (['active', 'trialing'].includes(normalizedStatus)) {
      return 'subscription-status-badge is-active';
    }
    if (['canceled', 'cancelled', 'unpaid', 'incomplete_expired'].includes(normalizedStatus)) {
      return 'subscription-status-badge is-danger';
    }
    return 'subscription-status-badge is-warning';
  };

  // --- EVENT HANDLERS ---
  const handleSaveChanges = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append('first_name', firstName);
      formData.append('last_name', lastName);
      formData.append('phone_number', phoneNumber);
      formData.append('language', language);
      formData.append('linkedin_profile', linkedin);
      formData.append('user_email', user.email);
      // Only append image if changed
      if (profilePictureFile) {
        formData.append('profile_picture', profilePictureFile);
      }
      const response = await fetch(`${API_BASE_URL}/api/user/update/`, {
        method: 'PUT',
        body: formData,
      });
      const tokenExpired = await checkTokenExpiration(
        response,
        setSessionExpired,
        setToast
      );
      if (tokenExpired) {
        setIsSaving(false);
        return;
      }
      const data = await response.json();
      if (!response.ok) {
        toastdisplay(
          data.detail || 'Failed to update profile.',
          false,
          3000,
          setToast
        );
        throw new Error(data.detail || 'Failed to update profile.');
      }
      setAuthenticatedSession(data.user);
      toastdisplay('Profile updated successfully', true, 3000, setToast);
    } catch (error) {
      toastdisplay(error.message, false, 3000, setToast);
    } finally {
      setIsSaving(false);
    }
  };

  const handleProfilePictureChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfilePicture(URL.createObjectURL(e.target.files[0]));
      setProfilePictureFile(e.target.files[0]);
    }
  };

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      await logout();
    }
  };

  const leaveAfterAccountDeletion = async () => {
    try {
      await requestBackendLogout();
    } catch {
      /* The account may already be gone. */
    }
    clearAllCaches();
    clearSyncableLocalStorage();
    localStorage.removeItem('userEmail');
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    publishAuthEvent(AUTH_EVENT_TYPES.logout, { reason: 'account_deleted' });
    window.location.href = '/login';
  };

  const handleDeleteAccount = async (event) => {
    event.preventDefault();
    if (!user?.email || isDeletingAccount) return;
    setIsDeletingAccount(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/account`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          confirmation: deleteConfirmation.trim(),
          phrase: deletePhrase.trim(),
          password: deletePassword,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data.detail || 'We could not delete your account. Please try again.'
        );
      }
      toastdisplay(
        data.message || 'Your Liwaza account has been deleted.',
        true,
        3000,
        setToast
      );
      await leaveAfterAccountDeletion();
    } catch (error) {
      toastdisplay(error.message, false, 4000, setToast);
      setIsDeletingAccount(false);
    }
  };

  const applyHoldPayload = (payload, hold) => {
    const nextHolds = payload?.holds || progressHolds;
    const nextCurrent = payload?.current || hold || null;
    setProgressHolds(nextHolds);
    setCurrentProgressHold(nextCurrent);
    persistProgressHoldsInSession({
      current: nextCurrent,
      holds: nextHolds,
    });
  };

  const handleSaveProgressHold = async (event) => {
    event.preventDefault();
    if (!holdStartDate || !holdEndDate) {
      toastdisplay('Choose a start and end date.', false, 3000, setToast);
      return;
    }
    setHoldSaving(true);
    try {
      const editableHold =
        currentProgressHold &&
        (currentProgressHold.status === 'scheduled' ||
          currentProgressHold.status === 'active')
          ? currentProgressHold
          : null;
      const result = editableHold
        ? await updateProgressHold(editableHold.id, {
            startDate: holdStartDate,
            endDate: holdEndDate,
            reason: holdReason,
          })
        : await createProgressHold({
            startDate: holdStartDate,
            endDate: holdEndDate,
            reason: holdReason,
          });
      const refreshed = await fetchProgressHolds();
      applyHoldPayload(refreshed, result.hold);
      toastdisplay(
        result.message || 'Progress hold saved.',
        true,
        4000,
        setToast
      );
    } catch (error) {
      toastdisplay(error.message, false, 4000, setToast);
    } finally {
      setHoldSaving(false);
    }
  };

  const handleCancelProgressHold = async () => {
    if (!currentProgressHold?.id) return;
    if (
      !window.confirm(
        'Resume progression now? Unused pause days will be cancelled.'
      )
    ) {
      return;
    }
    setHoldSaving(true);
    try {
      const result = await cancelProgressHold(currentProgressHold.id);
      const refreshed = await fetchProgressHolds();
      applyHoldPayload(refreshed, null);
      setHoldReason('');
      toastdisplay(
        result.message || 'Progress hold cancelled.',
        true,
        4000,
        setToast
      );
    } catch (error) {
      toastdisplay(error.message, false, 4000, setToast);
    } finally {
      setHoldSaving(false);
    }
  };

  // --- RENDER METHODS ---
  const renderPersonalInfo = () => (
    <form className="settings-form" onSubmit={handleSaveChanges}>
      <div className="settings-panel-header">
        <h2>Personal information</h2>
        <p>Update your identity, photo, and contact details.</p>
      </div>

      <div className="settings-card settings-avatar-card">
        <div className="settings-avatar">
          <ProfilePicture
            user={user}
            alt="Profile"
            context="Settings page"
            className="avatar-img"
          />
        </div>
        <div className="avatar-upload-section">
          <label htmlFor="avatar-upload" className="avatar-upload-btn">
            Change Profile Picture
          </label>
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleProfilePictureChange}
          />
          <p className="avatar-hint">
            JPG, PNG or GIF. Max size 1MB. Recommended 400x400px.
          </p>
        </div>
      </div>

      <div className="settings-card settings-fields-card">
        <div className="settings-fields-grid">
          <div className="settings-field">
            <label htmlFor="settings-first-name">First Name</label>
            <input
              id="settings-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter your first name"
            />
          </div>
          <div className="settings-field">
            <label htmlFor="settings-last-name">Last Name</label>
            <input
              id="settings-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter your last name"
            />
          </div>
          <div className="settings-field">
            <label htmlFor="settings-avatar-name">Avatar Name</label>
            <input
              id="settings-avatar-name"
              type="text"
              value={avatarName}
              disabled
              placeholder="Auto-generated display name"
            />
          </div>
          <div className="settings-field">
            <label htmlFor="settings-phone">Phone Number</label>
            <input
              id="settings-phone"
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+237 6 00 00 00 00"
              autoComplete="tel"
            />
          </div>
          <div className="settings-field">
            <label htmlFor="settings-email">Email Address</label>
            <input
              id="settings-email"
              type="email"
              value={user?.email || ''}
              disabled
              placeholder="Your email address"
            />
          </div>
          <div className="settings-field">
            <label htmlFor="settings-language">Language</label>
            <select
              id="settings-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="en">English</option>
              <option value="fr">French</option>
              <option value="es">Spanish</option>
              <option value="de">German</option>
            </select>
          </div>
          <div className="settings-field settings-field-wide">
            <label htmlFor="settings-linkedin">LinkedIn Profile</label>
            <input
              id="settings-linkedin"
              type="url"
              value={linkedin}
              onChange={(e) => setLinkedin(e.target.value)}
              placeholder="https://linkedin.com/in/yourprofile"
            />
          </div>
        </div>

        <div className="save-button-container">
          <button type="submit" className="btn-lwz-primary" disabled={isSaving}>
            {isSaving ? (
              <>
                <FiLoader className="loading-spinner" />
                Saving...
              </>
            ) : (
              <>
                <FiSave />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      <div className="settings-inline-actions">
        <button
          type="button"
          className="btn-lwz-secondary settings-signout-btn"
          onClick={handleSignOut}
        >
          <FiLogOut />
          Sign out
        </button>
      </div>
    </form>
  );

  const renderBilling = () => (
    <div className="settings-billing-panel">
      <div className="settings-panel-header">
        <h2>Billing & invoices</h2>
        <p>Review your plans, invoices, and billing cycle.</p>
      </div>

      <div className="billing-header">
        <div>
          <label htmlFor="billing-month-select">View month:</label>
          <select
            id="billing-month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {billingMonths.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
        </div>
        {subscription && subscription.manage_url && (
          <a
            href={subscription.manage_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-lwz-secondary"
          >
            <FiCreditCard />
            Manage Subscription
          </a>
        )}
      </div>

      {activePremiumSubscriptions.length > 0 && (
        <div className="subscription-info subscription-info-multi">
          <div className="subscription-info-heading">
            <div>
              <h3>
                <FiLayers
                  style={{
                    display: 'inline',
                    verticalAlign: 'middle',
                    marginRight: 8,
                  }}
                />
                Active premium subscriptions
              </h3>
              <p className="subscription-info-copy">
                Each plan is billed separately. If one ends, you keep access to
                the others until their own expiry date.
              </p>
            </div>
          </div>

          <div className="subscription-card-list">
            {activePremiumSubscriptions.map((sub) => (
              <article key={sub.id} className="subscription-card">
                <div className="subscription-card-header">
                  <div>
                    <span className="subscription-card-eyebrow">Package</span>
                    <h4>{sub.package_name || 'Premium'}</h4>
                  </div>
                  <span className={getSubscriptionStatusClassName(sub.status)}>
                    {formatSubscriptionStatus(sub.status)}
                  </span>
                </div>

                <div className="subscription-card-price-row">
                  <div>
                    <span className="subscription-card-eyebrow">Amount paid</span>
                    <p className="subscription-card-price">
                      {formatSubscriptionAmount(sub)}
                    </p>
                  </div>
                  <div className="subscription-card-cycle">
                    {sub.is_yearly ? 'Yearly plan' : 'Monthly plan'}
                    {sub.duration_months ? ` · ${sub.duration_months} mo` : ''}
                    {sub.is_trial ? ' · Trial' : ''}
                  </div>
                </div>

                <div className="subscription-card-details">
                  <div className="subscription-card-detail">
                    <span>Billing cycle</span>
                    <p>
                      {sub.is_yearly ? 'Yearly' : 'Monthly'}
                      {sub.duration_months ? ` · ${sub.duration_months} mo` : ''}
                    </p>
                  </div>

                  {sub.next_billing_date && (
                    <div className="subscription-card-detail">
                      <span>Next billing</span>
                      <p>{formatSubscriptionDate(sub.next_billing_date)}</p>
                    </div>
                  )}

                  {sub.end_date && (
                    <div className="subscription-card-detail">
                      <span>Access until</span>
                      <p>{formatSubscriptionDate(sub.end_date)}</p>
                    </div>
                  )}
                </div>

                {sub.services?.length > 0 && (
                  <div className="subscription-services">
                    <span className="subscription-card-eyebrow">
                      Included services
                    </span>
                    <div className="subscription-service-list">
                      {sub.services.map((s, i) => (
                        <div key={i} className="subscription-service-pill">
                          <strong>{s.name}</strong>
                          <span>{s.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      )}

      {subscription && activePremiumSubscriptions.length === 0 && (
        <div className="subscription-info">
          <h3>Current Subscription</h3>
          <div className="subscription-grid">
            <div className="subscription-item">
              <span>Package</span>
              <p>{subscription.package_name || 'Premium'}</p>
            </div>
            <div className="subscription-item">
              <span>Status</span>
              <p
                style={{
                  color:
                    subscription.status === 'active' ? '#4CAF50' : '#FF9800',
                }}
              >
                {subscription.status === 'active'
                  ? 'Active'
                  : subscription.status}
              </p>
            </div>
            <div className="subscription-item">
              <span>Billing Cycle</span>
              <p>
                {subscription.is_yearly ? 'Yearly' : 'Monthly'}
                {subscription.is_trial && ' (Trial)'}
              </p>
            </div>
            <div className="subscription-item">
              <span>Amount</span>
              <p>
                {subscription.amount?.toFixed(2)} {subscription.currency}
              </p>
            </div>
            {subscription.next_billing_date && (
              <div className="subscription-item">
                <span>Next Billing</span>
                <p>
                  {new Date(
                    subscription.next_billing_date
                  ).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="billing-table-container">
        {billingLoading ? (
          <div className="layout-loading">
            <div className="loading-spinner"></div>
            <p>Loading billing information...</p>
          </div>
        ) : billingError ? (
          <div style={{ color: 'var(--liwaza-error)', padding: 24 }}>
            {billingError}
          </div>
        ) : (
          <table className="billing-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice #</th>
                <th>Description</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Period</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      textAlign: 'center',
                      padding: 24,
                      color: 'var(--liwaza-on-surface-variant)',
                    }}
                  >
                    No invoices for this month.
                  </td>
                </tr>
              ) : (
                invoices.map((inv, idx) => (
                  <tr key={idx}>
                    <td>
                      {new Date(inv.date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td
                      style={{
                        fontSize: '0.9rem',
                        color: 'var(--liwaza-on-surface-variant)',
                      }}
                    >
                      {inv.invoice_number || '-'}
                    </td>
                    <td>{inv.description || 'Liwaza Subscription'}</td>
                    <td>
                      <span
                        className={`status-badge status-${inv.status?.toLowerCase()}`}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td>
                      {inv.amount?.toFixed(2)} {inv.currency}
                    </td>
                    <td
                      style={{
                        fontSize: '0.9rem',
                        color: 'var(--liwaza-on-surface-variant)',
                      }}
                    >
                      {inv.period_start && inv.period_end
                        ? `${new Date(inv.period_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - ${new Date(inv.period_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`
                        : '-'}
                    </td>
                    <td>
                      <a
                        href={inv.invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-lwz-secondary"
                        style={{
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.8rem',
                        }}
                      >
                        <FiFileText style={{ fontSize: '0.875rem' }} />
                        View
                      </a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const renderProgression = () => (
    <form className="settings-form" onSubmit={handleSaveProgressHold}>
      <div className="settings-panel-header">
        <h2>Progression pause</h2>
        <p>
          Freeze article, IELTS, and GRE daily progression for a date range.
          Your subscription and payment stay unchanged.
        </p>
      </div>

      {holdLoading ? (
        <p className="settings-subtitle">Loading your pause settings...</p>
      ) : (
        <>
          {currentProgressHold ? (
            <div className="settings-hold-status">
              <strong>
                {currentProgressHold.status === 'active'
                  ? 'Pause is active'
                  : 'Pause is scheduled'}
              </strong>
              <p>
                {currentProgressHold.start_date} to {currentProgressHold.end_date}
                {currentProgressHold.reason
                  ? ` · ${currentProgressHold.reason}`
                  : ''}
              </p>
            </div>
          ) : (
            <p className="settings-subtitle">
              No pause is currently scheduled.
            </p>
          )}

          <div className="settings-card settings-fields-card">
            <div className="settings-fields-grid">
              <div className="settings-field">
                <label htmlFor="hold-start-date">Start date</label>
                <input
                  id="hold-start-date"
                  type="date"
                  value={holdStartDate}
                  onChange={(e) => setHoldStartDate(e.target.value)}
                  disabled={currentProgressHold?.status === 'active'}
                />
              </div>
              <div className="settings-field">
                <label htmlFor="hold-end-date">End date</label>
                <input
                  id="hold-end-date"
                  type="date"
                  value={holdEndDate}
                  onChange={(e) => setHoldEndDate(e.target.value)}
                />
              </div>
              <div className="settings-field settings-field-wide">
                <label htmlFor="hold-reason">Reason (optional)</label>
                <input
                  id="hold-reason"
                  type="text"
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                  placeholder="Travel, exams, family..."
                />
              </div>
            </div>

            <div className="settings-hold-actions">
              <button type="submit" className="btn-lwz-primary" disabled={holdSaving}>
                {holdSaving ? (
                  <>
                    <FiLoader className="spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave />
                    {currentProgressHold ? 'Update pause' : 'Pause progression'}
                  </>
                )}
              </button>
              {currentProgressHold &&
              (currentProgressHold.status === 'scheduled' ||
                currentProgressHold.status === 'active') ? (
                <button
                  type="button"
                  className="btn-lwz-secondary"
                  onClick={handleCancelProgressHold}
                  disabled={holdSaving}
                >
                  Resume now
                </button>
              ) : null}
            </div>
          </div>

          {progressHolds.length > 0 ? (
            <div className="settings-hold-history">
              <h2>History</h2>
              <ul>
                {progressHolds.map((hold) => (
                  <li key={hold.id}>
                    {hold.start_date} → {hold.end_date} · {hold.status}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </form>
  );

  const renderToefl = () => (
    <div className="settings-form">
      <div className="settings-panel-header">
        <h2>TOEFL bootcamp</h2>
        <p>
          Choose your exam date, enroll, change the date, or return to IELTS
          here. This is the only place to start the TOEFL bootcamp.
        </p>
      </div>
      <div className="settings-card settings-fields-card">
        <ToeflActivation embedded />
      </div>
    </div>
  );

  const canConfirmDeletion =
    deleteAcknowledged &&
    deleteConfirmation.trim().toLowerCase() ===
      String(user?.email || '').trim().toLowerCase() &&
    deletePhrase.trim().toUpperCase() === 'DELETE' &&
    deletePassword.trim().length > 0;

  const renderPrivacy = () => (
    <div className="settings-privacy-panel">
      <div className="settings-panel-header">
        <h2>Privacy & data</h2>
        <p>
          You can erase your Liwaza account under GDPR. This permanently stops
          billing, emails, and reminders linked to you. Deletion needs your
          email, the word DELETE, and your password so it cannot happen by
          mistake.
        </p>
      </div>

      <div className="settings-privacy-card">
        <div className="settings-privacy-icon" aria-hidden="true">
          <FiShield />
        </div>
        <div>
          <h3>What gets stopped and deleted</h3>
          <ul>
            <li>Your profile, photo, phone number, and saved preferences</li>
            <li>Active subscriptions, including Stripe renewals</li>
            <li>Product emails, coaching reminders, and webinar reminders</li>
            <li>Marketing contacts and future Liwaza messages to this email</li>
          </ul>
        </div>
      </div>

      <div className="settings-danger-zone">
        <div>
          <p className="settings-danger-kicker">Danger zone</p>
          <h3>Delete my Liwaza account</h3>
          <p>
            This cannot be undone. Invoices already issued to Stripe may still
            exist for tax records, but Liwaza will no longer store or use your
            personal information.
          </p>
        </div>
        <button
          type="button"
          className="settings-delete-btn"
          onClick={() => {
            setDeleteConfirmation('');
            setDeletePhrase('');
            setDeletePassword('');
            setDeleteAcknowledged(false);
            setShowDeleteModal(true);
          }}
        >
          <FiTrash2 />
          Delete my data
        </button>
      </div>
    </div>
  );

  return (
    <div className="layout">
      <Sidebar />
      {/* Toast Notifications */}
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
          onClick={() => setToast({ ...toast, show: false })}
        >
          {toast.message}
        </div>
      )}
      <div className="content">
        <div className="settings-container">
          <header className="settings-page-header">
            <div>
              <p className="settings-kicker">Account</p>
              <h1 className="settings-title">Settings</h1>
              <p className="settings-subtitle">
                {isCoach
                  ? 'Manage your profile and privacy.'
                  : 'Manage your profile, billing, progression, TOEFL, and privacy.'}
              </p>
            </div>
          </header>

          <div className="settings-workspace">
            <div
              className="settings-tabs"
              role="tablist"
              aria-label="Settings sections"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'personal'}
                className={`settings-tab ${activeTab === 'personal' ? 'active' : ''}`}
                onClick={() => setActiveTab('personal')}
              >
                <FiUser />
                <span>Personal</span>
              </button>
              {isCoach ? null : (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'billing'}
                  className={`settings-tab ${activeTab === 'billing' ? 'active' : ''}`}
                  onClick={() => setActiveTab('billing')}
                >
                  <FiCreditCard />
                  <span>Billing</span>
                </button>
              )}
              {isCoach ? null : (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'progression'}
                  className={`settings-tab ${activeTab === 'progression' ? 'active' : ''}`}
                  onClick={() => {
                    setHoldLoading(true);
                    setActiveTab('progression');
                  }}
                >
                  <FiPause />
                  <span>Progression</span>
                </button>
              )}
              {isCoach ? null : (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'toefl'}
                  className={`settings-tab ${activeTab === 'toefl' ? 'active' : ''}`}
                  onClick={() => setActiveTab('toefl')}
                >
                  <FiBookOpen />
                  <span>TOEFL</span>
                </button>
              )}
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'privacy'}
                className={`settings-tab ${activeTab === 'privacy' ? 'active' : ''}`}
                onClick={() => setActiveTab('privacy')}
              >
                <FiShield />
                <span>Privacy</span>
              </button>
            </div>

            <div className="settings-content" role="tabpanel">
              {activeTab === 'personal' && renderPersonalInfo()}
              {!isCoach && activeTab === 'billing' && renderBilling()}
              {!isCoach && activeTab === 'progression' && renderProgression()}
              {!isCoach && activeTab === 'toefl' && renderToefl()}
              {activeTab === 'privacy' && renderPrivacy()}
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div
          className="settings-modal-backdrop"
          onClick={() => !isDeletingAccount && setShowDeleteModal(false)}
        >
          <div
            className="settings-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="settings-modal-close"
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeletingAccount}
              aria-label="Close"
            >
              <FiX />
            </button>
            <div className="settings-modal-icon" aria-hidden="true">
              <FiTrash2 />
            </div>
            <h2 id="delete-account-title">Delete your Liwaza account?</h2>
            <p>
              This will erase your personal data and immediately stop every
              subscription, email, and reminder linked to{' '}
              <strong>{user?.email}</strong>.
            </p>
            <form onSubmit={handleDeleteAccount}>
              <label htmlFor="delete-account-confirmation">
                Type your email to confirm
              </label>
              <input
                id="delete-account-confirmation"
                type="email"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                placeholder={user?.email || 'you@email.com'}
                autoComplete="off"
                disabled={isDeletingAccount}
              />
              <label htmlFor="delete-account-phrase">
                Type DELETE to confirm this is intentional
              </label>
              <input
                id="delete-account-phrase"
                type="text"
                value={deletePhrase}
                onChange={(event) => setDeletePhrase(event.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                disabled={isDeletingAccount}
              />
              <label htmlFor="delete-account-password">
                Enter your password
              </label>
              <input
                id="delete-account-password"
                type="password"
                value={deletePassword}
                onChange={(event) => setDeletePassword(event.target.value)}
                placeholder="Your password"
                autoComplete="current-password"
                disabled={isDeletingAccount}
              />
              <label className="settings-modal-check">
                <input
                  type="checkbox"
                  checked={deleteAcknowledged}
                  onChange={(event) => setDeleteAcknowledged(event.target.checked)}
                  disabled={isDeletingAccount}
                />
                I understand this cannot be undone and I will stop receiving
                Liwaza emails.
              </label>
              <div className="settings-modal-actions">
                <button
                  type="button"
                  className="btn-lwz-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeletingAccount}
                >
                  Keep my account
                </button>
                <button
                  type="submit"
                  className="settings-delete-btn"
                  disabled={!canConfirmDeletion || isDeletingAccount}
                >
                  {isDeletingAccount ? (
                    <>
                      <FiLoader className="loading-spinner" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FiTrash2 />
                      Delete permanently
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
